import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  User,
  UserCredential,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from './firebase.config';
import { mapFirebaseError } from '@/utils/errorHandling';
import { createUserProfile } from './user.service';

/**
 * Register a new user account
 * Creates both Firebase Auth account and Firestore user profile
 * @throws {Error} Mapped Firebase error with user-friendly message
 */
export async function register(email: string, password: string): Promise<UserCredential> {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Create user profile in Firestore
    await createUserProfile(userCredential.user.uid, email);

    return userCredential;
  } catch (error) {
    throw mapFirebaseError(error);
  }
}

/**
 * Authenticate existing user
 * @throws {Error} Mapped Firebase error with user-friendly message
 */
export async function login(email: string, password: string): Promise<UserCredential> {
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (error) {
    throw mapFirebaseError(error);
  }
}

/**
 * Sign in with Google (popup)
 * Also ensures a Firestore user profile exists for the account
 */
export async function loginWithGoogle(): Promise<UserCredential> {
  try {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    const credential = await signInWithPopup(auth, provider);

    // Ensure user profile exists (create if missing)
    if (credential.user?.uid && credential.user.email) {
      await createUserProfile(credential.user.uid, credential.user.email);
    }

    return credential;
  } catch (error) {
    throw mapFirebaseError(error);
  }
}

/**
 * Sign out current user
 * @throws {Error} Mapped Firebase error with user-friendly message
 */
export async function logout(): Promise<void> {
  try {
    await signOut(auth);
  } catch (error) {
    throw mapFirebaseError(error);
  }
}

/**
 * Get current authenticated user (synchronous)
 * @returns User object if authenticated, null otherwise
 */
export function getCurrentUser(): User | null {
  return auth.currentUser;
}

/**
 * Subscribe to authentication state changes
 * @param callback Function called when auth state changes
 * @returns Unsubscribe function to stop listening
 */
export function onAuthStateChanged(callback: (user: User | null) => void): () => void {
  return firebaseOnAuthStateChanged(auth, callback);
}
